import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query as firestoreQuery,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import "./PopuliPage.css";

const API = {
  process: "https://populi-document-upload-process-7kpxqstxwq-ey.a.run.app",
  download: "https://populi-getfile-795950092325.europe-west3.run.app",
};

const DOCUMENT_TYPES = [
  ["prosedur", "Prosedür / Politika Dokümanı"],
  ["faq", "Sıkça Sorulan Sorular (SSS)"],
  ["iletisim", "İletişim Bilgileri"],
  ["egitim", "Eğitim Dokümanı"],
  ["operasyon", "Operasyon Dokümanı"],
  ["bt_dokuman", "IT Dokümanı"],
  ["other", "Diğer"],
];
const DOCUMENT_TYPE_LABELS = Object.fromEntries(DOCUMENT_TYPES);

const ACCESS_ROLES = ["Merkez", "Saha", "MİM"];
const FOLDERS_COLLECTION = "folders";
const DOCUMENTS_COLLECTION = "populi-documents";

async function getCurrentDepartment(userEmail) {
  if (auth.currentUser?.uid) {
    const snapshot = await getDoc(doc(db, "users", auth.currentUser.uid));
    const department = snapshot.exists() ? snapshot.data().department || "" : "";
    if (department) return department;
  }

  if (!userEmail) return "";
  const snapshot = await getDocs(firestoreQuery(
    collection(db, "users"),
    where("email", "==", userEmail.trim().toLowerCase()),
    limit(1),
  ));
  return snapshot.docs[0]?.data().department || "";
}

async function getFolderRows(department) {
  const foldersSnapshot = await getDocs(firestoreQuery(
    collection(db, FOLDERS_COLLECTION),
    where("organisationDept", "==", department),
  ));
  const folders = foldersSnapshot.docs
    .map((folderDoc) => ({ id: folderDoc.id, ...folderDoc.data() }))
    .filter((folder) => folder.organisationDept === department);

  const rows = await Promise.all(folders.map(async (folder) => {
    const documentsSnapshot = await getDocs(firestoreQuery(
      collection(db, DOCUMENTS_COLLECTION),
      where("projectID", "==", folder.projectID),
    ));
    const documents = documentsSnapshot.docs
      .map((documentDoc) => ({ id: documentDoc.id, ...documentDoc.data() }))
      .filter((document) => document.status !== "deleted");

    if (!documents.length) {
      return [{
        ...folder,
        documentType: null,
        documentName: null,
        documentID: null,
        status: null,
        process_time: null,
      }];
    }

    return documents.map((document) => ({
      ...folder,
      documentType: document.documentType,
      documentName: document.documentName,
      documentID: document.documentID || document.id,
      filelocation: document.filelocation,
      status: document.status,
      process_time: document.process_time,
    }));
  }));

  return rows.flat();
}

function timestampValue(value) {
  if (value?.toMillis instanceof Function) return value.toMillis();
  if (value?.toDate instanceof Function) return value.toDate().getTime();
  const timestamp = new Date(value || 0).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function Icon({ name }) {
  const paths = {
    plus: <path d="M12 5v14M5 12h14" />,
    refresh: <path d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7" />,
    upload: <path d="M12 16V4m0 0L7 9m5-5 5 5M5 15v5h14v-5" />,
    download: <path d="M12 4v12m0 0-5-5m5 5 5-5M5 20h14" />,
    trash: <path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" />,
    close: <path d="m6 6 12 12M18 6 6 18" />,
    folder: <path d="M3 7h7l2 2h9v10H3V7Z" />,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    document: <path d="M6 3h8l4 4v14H6V3Zm8 0v5h5M9 12h6M9 16h6" />,
  };

  return (
    <svg className="populi-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="populi-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="populi-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="populi-modal__header">
          <h2>{title}</h2>
          <button className="populi-icon-button" type="button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function appendFormData(values) {
  const data = new FormData();
  Object.entries(values).forEach(([key, value]) => data.append(key, value));
  return data;
}

async function postForm(url, values) {
  const response = await fetch(url, { method: "POST", body: appendFormData(values) });
  if (!response.ok) throw new Error(`İstek başarısız (${response.status})`);
  return response.json();
}

export default function PopuliPage({ userEmail: emailProp, onUnauthorized }) {
  const [projects, setProjects] = useState([]);
  const [department, setDepartment] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);

  const userEmail =
    emailProp || (typeof window !== "undefined" ? sessionStorage.getItem("userEmail") : "") || "";

  const loadProjects = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    setError("");
    try {
      const currentDepartment = await getCurrentDepartment(userEmail);
      if (!currentDepartment) {
        throw new Error("Kullanıcının organizasyon bilgisi bulunamadı.");
      }
      const folderRows = await getFolderRows(currentDepartment);
      setDepartment(currentDepartment);
      setProjects(folderRows);
    } catch (requestError) {
      setError(requestError.message || "Dokümanlar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (!userEmail) {
      onUnauthorized?.();
      return;
    }
    loadProjects();
  }, [loadProjects, onUnauthorized, userEmail]);

  async function openFolderModal() {
    setLoading(true);
    setError("");
    try {
      const currentDepartment = await getCurrentDepartment(userEmail);
      if (!currentDepartment) {
        throw new Error("Kullanıcının organizasyon bilgisi bulunamadı.");
      }
      setDepartment(currentDepartment);
      setFolderModalOpen(true);
    } catch (requestError) {
      setError(requestError.message || "Organizasyon bilgisi yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  const groups = useMemo(() => {
    const grouped = projects.reduce((result, item) => {
      const key = item.projectID;
      if (!result[key]) result[key] = [];
      result[key].push(item);
      return result;
    }, {});

    return Object.values(grouped)
      .map((items) => ({
        items: [...items].sort(
          (a, b) => timestampValue(b.process_time) - timestampValue(a.process_time),
        ),
      }))
      .sort(
        (a, b) =>
          timestampValue(b.items[0]?.createDate) - timestampValue(a.items[0]?.createDate),
      )
      .filter(({ items }) => {
        const first = items[0] || {};
        const searchable = [
          first.projectName || first.projectname,
          first.projectCode,
          first.organisationDept,
          first.accessRole,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("tr");
        return searchable.includes(query.trim().toLocaleLowerCase("tr"));
      });
  }, [projects, query]);
  const documentCount = projects.filter((item) => item.documentID != null).length;

  async function createFolder(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const projectName = String(form.get("projectName") || "").trim();
    if (!projectName) return;
    if (!department) {
      setError("Kullanıcının organizasyon bilgisi bulunamadı.");
      return;
    }

    const accessRole = String(form.get("dataCenter") || "");
    const organisationDept = department;
    const projectID = `project${Date.now()}`;
    setLoading(true);
    setError("");
    try {
      await setDoc(doc(db, "folders", projectID), {
        projectID,
        projectName,
        useremail: userEmail,
        organisationDept,
        botname: "YiviBot",
        accessRole,
        createDate: serverTimestamp(),
      });
      setProjects((currentProjects) => [
        ...currentProjects,
        {
          projectID,
          projectName,
          useremail: userEmail,
          organisationDept,
          botname: "YiviBot",
          accessRole,
          createDate: new Date().toISOString(),
          documentID: null,
        },
      ]);
      setFolderModalOpen(false);
    } catch (requestError) {
      setError(requestError.message || "Klasör oluşturulamadı.");
    } finally {
      setLoading(false);
    }
  }

  async function uploadDocument(event) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const file = form.get("file");
    const documentName = String(form.get("projectdocumentName") || "").trim();
    if (!documentName || !(file instanceof File) || !file.name) return;

    setLoading(true);
    setError("");
    try {
      await postForm(API.process, {
        projectID: uploadTarget.projectID,
        useremail: userEmail,
        projectdocumentName: documentName,
        projectDocument: form.get("projectDocument"),
        accessRole: uploadTarget.accessRole || "",
        botname: uploadTarget.botname || "YiviBot",
        fileExtension: file.name.split(".").pop() || "",
        file,
      });

      setUploadTarget(null);
      await loadProjects();
    } catch (requestError) {
      setError(requestError.message || "Belge yüklenemedi.");
      setLoading(false);
    }
  }

  async function removeItem(projectID, documentID, type) {
    if (!window.confirm("Silme işlemini onaylıyor musunuz?")) return;
    setLoading(true);
    setError("");
    try {
      const documentsSnapshot = await getDocs(firestoreQuery(
        collection(db, DOCUMENTS_COLLECTION),
        where("projectID", "==", projectID),
      ));
      const documents = documentsSnapshot.docs
        .map((documentDoc) => ({ id: documentDoc.id, ...documentDoc.data() }));

      if (type === 1) {
        const activeDocuments = documents.filter((document) => document.status !== "deleted");
        if (activeDocuments.length) {
          throw new Error("İçinde belge bulunan klasör silinemez. Önce belgeleri silmelisiniz.");
        }
        await deleteDoc(doc(db, FOLDERS_COLLECTION, projectID));
        setProjects((currentProjects) => (
          currentProjects.filter((item) => item.projectID !== projectID)
        ));
      } else {
        const matchingDocuments = documents.filter((document) => (
          document.documentID === documentID || document.id === documentID
        ));
        if (!matchingDocuments.length) throw new Error("Silinecek belge bulunamadı.");

        await Promise.all(matchingDocuments.map((document) => (
          updateDoc(doc(db, DOCUMENTS_COLLECTION, document.id), { status: "deleted" })
        )));
        setProjects((currentProjects) => {
          const removedItem = currentProjects.find((item) => (
            item.projectID === projectID && item.documentID === documentID
          ));
          const remainingProjects = currentProjects.filter((item) => !(
            item.projectID === projectID && item.documentID === documentID
          ));
          if (!removedItem || remainingProjects.some((item) => item.projectID === projectID)) {
            return remainingProjects;
          }
          return [...remainingProjects, {
            ...removedItem,
            documentType: null,
            documentName: null,
            documentID: null,
            status: null,
            process_time: null,
          }];
        });
      }
    } catch (requestError) {
      setError(requestError.message || "Kayıt silinemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function downloadDocument(filelocation) {
    setLoading(true);
    setError("");
    try {
      if (typeof filelocation !== "string" || !filelocation.trim()) {
        throw new Error("Belgenin dosya konumu bulunamadı.");
      }
      const data = await postForm(API.download, { filelocation });
      if (!data?.signed_url) throw new Error("İndirme bağlantısı alınamadı.");
      const link = document.createElement("a");
      link.href = data.signed_url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (requestError) {
      setError(requestError.message || "Dosya indirilemedi.");
    } finally {
      setLoading(false);
    }
  }

  if (!userEmail) {
    return <div className="populi-page populi-empty">Bu sayfayı görüntülemek için giriş yapmalısınız.</div>;
  }

  return (
    <main className="populi-page">
      <section className="populi-shell">
        <header className="populi-page-header">
          <div>
            <span className="populi-eyebrow">{department || "Doküman Yönetimi"}</span>
            <h1>Doküman Merkezi</h1>
            <p>Klasörlerinizi yönetin, belgelerinizi güvenle yükleyin ve ihtiyaç duyduğunuz içeriğe hızla ulaşın.</p>
          </div>
          <div className="populi-summary" aria-label="Doküman özeti">
            <div><strong>{groups.length}</strong><span>Klasör</span></div>
            <div><strong>{documentCount}</strong><span>Belge</span></div>
          </div>
        </header>

        <div className="populi-toolbar">
          <div className="populi-search">
            <Icon name="search" />
            <label className="populi-visually-hidden" htmlFor="populi-project-search">Klasörlerde ara</label>
            <input
              id="populi-project-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Klasör adı, organizasyon veya erişim ara..."
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Aramayı temizle">
                <Icon name="close" />
              </button>
            )}
          </div>
          <div className="populi-toolbar__actions">
            <button className="populi-button populi-button--secondary" onClick={loadProjects} disabled={loading}>
              <Icon name="refresh" /> Yenile
            </button>
            <button
              className="populi-button populi-button--primary"
              onClick={openFolderModal}
              disabled={loading}
            >
              <Icon name="plus" /> Yeni Klasör
            </button>
          </div>
        </div>

        {error && <div className="populi-alert" role="alert">{error}</div>}

        <div className="populi-folder-list">
          {!loading && groups.length === 0 && (
            <div className="populi-empty-state">
              <span className="populi-empty-state__icon"><Icon name={query ? "search" : "folder"} /></span>
              <h2>{query ? "Eşleşen klasör bulunamadı" : "Henüz klasör bulunmuyor"}</h2>
              <p>{query ? "Arama ifadenizi değiştirip tekrar deneyin." : "Belgelerinizi düzenlemeye başlamak için ilk klasörünüzü oluşturun."}</p>
              {!query && (
                <button className="populi-button populi-button--primary" onClick={openFolderModal}>
                  <Icon name="plus" /> İlk klasörü oluştur
                </button>
              )}
            </div>
          )}

          {groups.map(({ items }) => {
            const folder = items.find((item) => item.documentID == null) || items[0];
            const documents = items.filter((item) => item.documentID != null);
            const folderName = folder.projectName || folder.projectname || "Adsız klasör";
            return (
              <article className="populi-folder-card" key={folder.projectID}>
                <header className="populi-folder-card__header">
                  <div className="populi-folder-heading">
                    <span className="populi-folder-icon"><Icon name="folder" /></span>
                    <div>
                      <h2>{folderName}</h2>
                      <div className="populi-folder-meta">
                        <span>{folder.organisationDept || "Organizasyon yok"}</span>
                        <span>{folder.accessRole || "Erişim belirtilmemiş"}</span>
                        <span>{documents.length} belge</span>
                      </div>
                    </div>
                  </div>
                  <div className="populi-folder-actions">
                    <button
                      className="populi-button populi-button--upload"
                      onClick={() => setUploadTarget(folder)}
                    >
                      <Icon name="upload" /> Belge Yükle
                    </button>
                    <button
                      className="populi-icon-button populi-icon-button--danger"
                      onClick={() => removeItem(folder.projectID, 1, 1)}
                      aria-label={`${folderName} sil`}
                      title="Klasörü sil"
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </header>

                {documents.length === 0 ? (
                  <div className="populi-folder-empty">
                    <Icon name="document" />
                    <div><strong>Belge eklenmemiş.</strong><span>Bu klasöre ilk belgenizi yükleyebilirsiniz.</span></div>
                  </div>
                ) : (
                  <div className="populi-table-wrap">
                    <table className="populi-table">
                      <thead><tr><th>Belge</th><th>Tür</th><th>Durum</th><th><span className="populi-visually-hidden">İşlemler</span></th></tr></thead>
                      <tbody>
                        {documents.map((item) => (
                          <tr key={item.documentID}>
                            <td data-label="Belge"><div className="populi-document-name"><Icon name="document" /><strong>{item.documentName || "Adsız belge"}</strong></div></td>
                            <td data-label="Tür">{DOCUMENT_TYPE_LABELS[item.documentType] || item.documentType || "—"}</td>
                            <td data-label="Durum"><span className="populi-status">{item.status || "—"}</span></td>
                            <td data-label="İşlemler">
                              <div className="populi-document-actions">
                                <button
                                  className="populi-icon-button populi-icon-button--success"
                                  onClick={() => downloadDocument(item.filelocation)}
                                  aria-label={`${item.documentName || "Belgeyi"} indir`}
                                  title="Belgeyi indir"
                                >
                                  <Icon name="download" />
                                </button>
                                <button
                                  className="populi-icon-button populi-icon-button--danger"
                                  onClick={() => removeItem(item.projectID, item.documentID, 2)}
                                  aria-label={`${item.documentName || "Belgeyi"} sil`}
                                  title="Belgeyi sil"
                                >
                                  <Icon name="trash" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      {folderModalOpen && (
        <Modal title="Yeni Klasör" onClose={() => setFolderModalOpen(false)}>
          <form className="populi-form" onSubmit={createFolder}>
            <label>Klasör Adı<input name="projectName" required autoFocus /></label>
            <label>
              Doküman Erişimi
              <select name="dataCenter" required defaultValue={ACCESS_ROLES[0]}>
                {ACCESS_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
              </select>
            </label>
            <label>
              Organizasyon
              <input
                name="organisationDept"
                value={department}
                readOnly
              />
            </label>
            <div className="populi-form__actions">
              <button type="button" className="populi-button populi-button--ghost" onClick={() => setFolderModalOpen(false)}>İptal</button>
              <button type="submit" className="populi-button populi-button--primary">Oluştur</button>
            </div>
          </form>
        </Modal>
      )}

      {uploadTarget && (
        <Modal title="Belge Yükle" onClose={() => setUploadTarget(null)}>
          <form className="populi-form" onSubmit={uploadDocument}>
            <label>Belge Adı<input name="projectdocumentName" required autoFocus /></label>
            <label>
              Belge Türü
              <select name="projectDocument" defaultValue={DOCUMENT_TYPES[0][0]}>
                {DOCUMENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>Dosya<input name="file" type="file" accept=".csv,.txt,.docx,.pdf" required /></label>
            <div className="populi-form__actions">
              <button type="button" className="populi-button populi-button--ghost" onClick={() => setUploadTarget(null)}>İptal</button>
              <button type="submit" className="populi-button populi-button--primary">Yükle</button>
            </div>
          </form>
        </Modal>
      )}

      {loading && <div className="populi-loading" role="status"><span /> İşlem yapılıyor…</div>}
    </main>
  );
}
